/**
 * AnalyticsProvider — wraps the authenticated app tree.
 *
 * Responsibilities:
 *  1. Initialize / destroy the analytics queue
 *  2. Auto-track React Router route changes as page_view events
 *  3. Heartbeat every 5 min to keep the server session alive
 *  4. Expose tracking methods to descendants via context
 */
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useAnalytics, { initAnalytics, destroyAnalytics } from '../hooks/useAnalytics';
import apiClient from '../apiClient';

const AnalyticsContext = createContext(null);

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function AnalyticsProvider({ children }) {
  const location = useLocation();
  const analytics = useAnalytics();
  const prevPath = useRef(null);

  // ── Init / teardown ──────────────────────────────
  useEffect(() => {
    initAnalytics();
    return () => destroyAnalytics();
  }, []);

  // ── Route change tracking ────────────────────────
  useEffect(() => {
    // Skip duplicate fires for the same path
    if (location.pathname === prevPath.current) return;
    prevPath.current = location.pathname;

    analytics.trackPageView(location.pathname);
  }, [location.pathname, analytics]);

  // ── Session heartbeat ────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      // Only heartbeat if the tab is visible and user appears active
      if (document.visibilityState !== 'visible') return;

      apiClient
        .post('/analytics/events', { eventType: 'heartbeat', route: location.pathname })
        .catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(id);
  }, [location.pathname]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * Convenience hook — returns { trackAction, trackSearch, trackFormSubmit, trackPageView }
 * Returns no-op stubs when used outside the provider (e.g., login page).
 */
export function useAnalyticsContext() {
  const ctx = useContext(AnalyticsContext);
  if (ctx) return ctx;

  // Safe fallback — analytics won't fire outside the provider
  return {
    trackPageView: () => {},
    trackAction: () => {},
    trackSearch: () => {},
    trackFormSubmit: () => {},
  };
}

export default AnalyticsProvider;
